/**
 * Pure decision helpers for the OMON-135 carer-guidance per-level
 * override mechanism. Extracted from plan-builder.ts so vitest can
 * exercise the policy without dragging in Firestore client init.
 *
 * Same extraction pattern as `row-search.ts` + `person-name.ts`.
 *
 * ## The problem these helpers exist to prevent
 *
 * The original implementation had three sites where a race or stale
 * read could plant `_carerGuidance: ["", "", "", "", ""]` on a saved
 * plan. Once stored, the read path preferred the (empty) snapshot
 * over the (rich) canonical content, so the textareas rendered blank
 * and every level was flagged "(edited)". Three layers of defence:
 *
 *   1. `chooseCarerGuidanceForLoad` — at plan load: if the stored
 *      snapshot is all-empty, fall back to canonical
 *   2. `chooseCarerGuidanceForSnapshot` — at goal-add: if canonical
 *      is empty (race), DON'T snapshot; let the load path recover
 *      next time
 *   3. `isMeaningfulOverride` — at save: refuse to persist
 *      `_carerGuidance` when the in-memory state is all-empty
 *
 * Together these make the "blank guidance after reload" bug
 * structurally impossible. Each layer is independently sufficient
 * for new plans; the load-side layer is what repairs already-broken
 * plans on next load.
 */

/**
 * True when every element is `""` or whitespace-only.
 * An empty array is also considered "all empty".
 */
export function isCarerGuidanceAllEmpty(arr: readonly string[]): boolean {
  if (arr.length === 0) return true;
  return arr.every((s) => typeof s !== "string" || s.trim() === "");
}

/**
 * Decide what `obj.carerGuidance` should be when loading a goal from
 * a saved plan doc.
 *
 * @param stored The `_carerGuidance` field as read from the Goal doc.
 *               Untyped because Firestore data is `unknown` until proven.
 * @param canonical The 5-string canonical guidance from
 *               `_activityDefinition.documentation[i].display`.
 *
 * Returns the canonical fallback when:
 *   - `stored` is null/undefined/not-an-array
 *   - `stored` has the wrong length (not exactly 5)
 *   - `stored` is all empty/whitespace strings (the bug shape)
 *
 * Returns a defensive copy of `stored` otherwise.
 */
export function chooseCarerGuidanceForLoad(
  stored: unknown,
  canonical: readonly string[],
): string[] {
  if (!Array.isArray(stored)) return [...canonical];
  if (stored.length !== 5) return [...canonical];
  if (stored.some((s) => typeof s !== "string")) return [...canonical];
  if (isCarerGuidanceAllEmpty(stored as string[])) return [...canonical];
  return [...(stored as string[])];
}

/**
 * Decide whether to take a canonical snapshot when adding a goal to
 * the plan builder.
 *
 * Returns the canonical 5-string array when at least one level is
 * non-empty (i.e. the catalogue actually has guidance to copy).
 *
 * Returns `undefined` when canonical is all empty, telling the caller
 * NOT to populate `obj.carerGuidance` at-add-time. The load-side
 * recovery (`chooseCarerGuidanceForLoad`) will fill it from canonical
 * on next render — by which time `state.activityData` is more likely
 * to be loaded.
 */
export function chooseCarerGuidanceForSnapshot(
  canonical: readonly string[],
): string[] | undefined {
  if (canonical.length !== 5) return undefined;
  if (isCarerGuidanceAllEmpty(canonical)) return undefined;
  return [...canonical];
}

/**
 * Predicate guarding the save-side write of `_carerGuidance`.
 *
 * Returns `true` only when:
 *   - in-memory `obj.carerGuidance` is a 5-string array
 *   - at least one element is non-empty (rules out the bug shape)
 *   - at least one element differs from canonical (a real edit)
 *
 * Returns `false` for all-empty arrays even if they "differ" from
 * a non-empty canonical, because an all-empty override is never the
 * librarian's actual intent — it's a stale-read artefact.
 */
export function isMeaningfulOverride(
  current: readonly string[] | undefined,
  canonical: readonly string[],
): boolean {
  if (!Array.isArray(current)) return false;
  if (current.length !== 5) return false;
  if (isCarerGuidanceAllEmpty(current)) return false;
  for (let i = 0; i < 5; i++) {
    if ((current[i] || "") !== (canonical[i] || "")) return true;
  }
  return false;
}

// ── Ed 2026-09-29: the per-plan override as ONE text ─────────────────────────

/**
 * The plan's one-text override when a plan is opened: its saved
 * `_carerGuidanceText`, else its legacy 5-level `_carerGuidance` composed —
 * but only when that is a real edit (`isMeaningfulOverride`), so a stale
 * copy of the catalogue never reappears as "this person's" text. null = the
 * plan uses the catalogue's guidance.
 */
export function planGuidanceOverrideOnLoad(
  storedText: unknown,
  storedLevels: unknown,
  canonicalLevels: readonly string[],
  labels: readonly string[],
  compose: (levels: readonly string[], labels: readonly string[]) => string,
): string | null {
  if (typeof storedText === "string" && storedText.trim()) return storedText;
  const levels = chooseCarerGuidanceForLoad(storedLevels, canonicalLevels);
  if (!isMeaningfulOverride(levels, canonicalLevels)) return null;
  return compose(levels, labels).trim() ? compose(levels, labels) : null;
}

/**
 * What to write as `_goal._carerGuidanceText` at save: the override when it
 * is non-blank and differs from the catalogue text; otherwise nothing, so the
 * goal late-binds to the catalogue (`feedback_late_binding_default`).
 */
export function planGuidanceTextToWrite(override: string | null | undefined, catalogueText: string): string | undefined {
  if (typeof override !== "string" || !override.trim()) return undefined;
  return override === catalogueText ? undefined : override;
}
